import { useRef, useState, useEffect } from 'react'
import TeamCard from './TeamCard'
import { useLanguage } from '../context/LanguageContext'

function TeamSection() {
  const { t } = useLanguage()
  const trackRef = useRef(null)
  const [teamMembers, setTeamMembers] = useState([])

  useEffect(() => {
    fetch('/api/team')
      .then(res => res.json())
      .then(data => setTeamMembers(Array.isArray(data) ? data : []))
      .catch(err => console.error('Error fetching team members:', err))
  }, [])

  // Desktop only: pause on mouse hover (mouseenter/mouseleave never fire on touch)
  const pauseTrack  = () => { if (trackRef.current) trackRef.current.style.animationPlayState = 'paused' }
  const resumeTrack = () => { if (trackRef.current) trackRef.current.style.animationPlayState = 'running' }

  if (!teamMembers || teamMembers.length === 0) return null;

  return (
    <div style={{ backgroundColor: '#FFFFFF', width: '100%' }}>
      <style>{`
        /* ── Team Marquee ── */
        .team-marquee-container {
          width: 100%;
          overflow: hidden;
          position: relative;
          padding: 8px 0 20px 0;
        }

        /* Gradient fading edges */
        .team-marquee-container::before,
        .team-marquee-container::after {
          content: '';
          position: absolute;
          top: 0;
          width: 180px;
          height: 100%;
          z-index: 2;
          pointer-events: none;
        }
        .team-marquee-container::before {
          left: 0;
          background: linear-gradient(to right, #ffffff 0%, rgba(255,255,255,0) 100%);
        }
        .team-marquee-container::after {
          right: 0;
          background: linear-gradient(to left, #ffffff 0%, rgba(255,255,255,0) 100%);
        }

        .team-marquee-track {
          display: flex;
          width: max-content;
          animation: team-scroll 28s linear infinite;
          gap: 24px;
        }

        /* Pause is handled via JS mouse events — NOT CSS :hover
           so touch devices keep scrolling uninterrupted */

        @keyframes team-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        .team-marquee-card {
          flex: 0 0 280px;
          width: 280px;
          cursor: pointer;
        }

        @media (max-width: 540px) {
          .team-marquee-card { flex: 0 0 240px; width: 240px; }
          .team-marquee-container::before,
          .team-marquee-container::after { width: 60px; }
        }
      `}</style>

      {/* Heading row */}
      <div style={{
        maxWidth: '1300px',
        margin: '0 auto',
        padding: '60px 20px 36px 20px',
        boxSizing: 'border-box',
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'flex-end' }}>
          {/* Left: Tag + Title */}
          <div style={{ flex: '1 1 auto' }}>
            <div style={{ marginBottom: '8px' }}>
              <span style={{
                backgroundColor: 'rgba(197,160,89,0.1)',
                color: '#C5A059',
                fontFamily: "'Poppins', sans-serif",
                fontSize: '12px',
                fontWeight: 700,
                borderRadius: '5px',
                padding: '5px 8px',
                display: 'inline-block',
              }}>{ t('team.tag') }</span>
            </div>
            <h2 style={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: 'clamp(23px, 3.5vw, 44px)',
              fontWeight: 600,
              lineHeight: '1.2em',
              letterSpacing: '-0.3px',
              color: '#002E53',
              margin: 0,
              maxWidth: '550px',
            }}>
              { t('team.heading') }
            </h2>
          </div>

          {/* Right: Description */}
          <div style={{ flex: '0 1 400px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
            <p style={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '1.6em',
              color: '#002E53',
              margin: 0,
              opacity: 0.75,
            }}>
              { t('team.desc') }
            </p>
          </div>
        </div>
      </div>

      {/* Infinite marquee belt — mouse events pause; touch events are ignored */}
      <div
        className="team-marquee-container"
        onMouseEnter={pauseTrack}
        onMouseLeave={resumeTrack}
      >
        <div ref={trackRef} className="team-marquee-track">
          {/* Double the list for seamless looping */}
          {[...teamMembers, ...teamMembers].map((agent, i) => (
            <div key={`${agent.id}-${i}`} className="team-marquee-card">
              <TeamCard member={agent} />
            </div>
          ))}
        </div>
      </div>

      {/* Button to view all */}
      <div style={{ textAlign: 'center', padding: '10px 20px 60px' }}>
        <a href="/contact" style={{
          display: 'inline-flex', alignItems: 'center', gap: '8px',
          fontFamily: "'Poppins', sans-serif", fontSize: '15px', fontWeight: 600,
          color: '#FFFFFF', backgroundColor: '#C5A059',
          padding: '14px 32px', borderRadius: '8px',
          textDecoration: 'none', transition: 'background 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#d4af6a'}
        onMouseLeave={e => e.currentTarget.style.backgroundColor = '#C5A059'}
        >
          { t('team.btn') }
        </a>
      </div>
    </div>
  )
}

export default TeamSection
